import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Stack,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BhiDialog } from 'src/UIKit';
import { useCreateCampaignPresetApi } from 'src/api/campaignPresets';
import { buildPresetContent } from '../../helpers/buildPresetContent';
import { DEFAULT_PRESET_INCLUDES, PresetIncludes } from '../../types';
import { getSectionsForType } from 'src/modules/campaignConfigBuilder/registry/sectionRegistry';
import { CampaignConfigType } from 'src/modules/campaignConfigBuilder/types';
import { useCampaignCreateOptionsLoader } from 'src/modules/campaignCreateOptions/hooks';
import { getPresetAudienceField } from '../../helpers/getPresetAudienceField';
import { usePresetNameDescriptionForm } from '../../helpers/usePresetNameDescriptionForm';

type Props = {
  open: boolean;
  companyId: string;
  campaignType: string;
  sourceConfigId: string | null;
  formValues: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

// Dialog that turns the current campaign builder state into a reusable
// campaign preset. The user picks which parts of the config to include.
export const SaveAsPresetDialog: React.FC<Props> = ({
  open,
  companyId,
  campaignType,
  sourceConfigId,
  formValues,
  onClose,
  onSaved,
}) => {
  const createPreset = useCreateCampaignPresetApi();
  const { loadCreateOptions } = useCampaignCreateOptionsLoader();

  const [includes, setIncludes] = useState<PresetIncludes>(DEFAULT_PRESET_INCLUDES);

  // The dialog stays mounted while the builder is open, so re-seed the include
  // toggles each time it opens — otherwise it shows leftover toggles from a
  // prior open. (Name/description re-seeding is handled by the shared hook.)
  useEffect(() => {
    if (open) {
      setIncludes(DEFAULT_PRESET_INCLUDES);
    }
  }, [open]);

  const toggleInclude = (key: keyof PresetIncludes) => {
    setIncludes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const audienceField = getPresetAudienceField(campaignType);
  // Keywords are only meaningful for types whose config has a keywords section.
  // Derive this from the single source of truth (SECTION_REGISTRY) rather than
  // a duplicated literal list, so a new keyword-enabled type only needs a
  // registry row. Unknown types resolve to [] → keywords hidden.
  const showKeywords = getSectionsForType(campaignType as CampaignConfigType).includes('keywords');

  // Sections whose checkbox is hidden for this campaign type must never be
  // persisted, even though their default include-flag may be true.
  const effectiveIncludes = {
    ...includes,
    keywords: showKeywords && includes.keywords,
    audience: !!audienceField && includes.audience,
  };
  const anyIncluded = Object.values(effectiveIncludes).some(Boolean);

  const { name, setName, description, setDescription, saving, canSave, handleSave } =
    usePresetNameDescriptionForm({
      open,
      initialName: '',
      initialDescription: '',
      logMessage: 'Failed to save campaign preset:',
      toastMessage: 'Failed to save template. Please try again.',
      onSave: async ({ name: trimmedName, description: trimmedDescription }) => {
        const content = buildPresetContent(formValues, effectiveIncludes);
        await createPreset(
          {
            name: trimmedName,
            description: trimmedDescription,
            type: campaignType,
            sourceConfigId,
            content,
          },
          companyId,
        );
        await loadCreateOptions(true, companyId);
        await onSaved();
        onClose();
      },
    });

  return (
    <BhiDialog open={open} onClose={onClose} title="Save as Custom Template" fullWidth>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus
            placeholder="Give this template a short, recognizable name"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="Describe what this template is for"
            InputLabelProps={{ shrink: true }}
          />

          <FormGroup>
            {showKeywords && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includes.keywords}
                    onChange={() => toggleInclude('keywords')}
                  />
                }
                label="Keywords"
              />
            )}
            {audienceField && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includes.audience}
                    onChange={() => toggleInclude('audience')}
                  />
                }
                label={audienceField.label}
              />
            )}
            <FormControlLabel
              control={
                <Checkbox checked={includes.messages} onChange={() => toggleInclude('messages')} />
              }
              label="Messages & content"
            />
            <FormControlLabel
              control={
                <Checkbox checked={includes.optOut} onChange={() => toggleInclude('optOut')} />
              }
              label="Opt-out message"
            />
          </FormGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || !anyIncluded || saving}
        >
          Save Template
        </Button>
      </DialogActions>
    </BhiDialog>
  );
};

export default SaveAsPresetDialog;
